import React from 'react';
import styled from 'styled-components';
import { Button } from '../atoms/Button';

export interface DocumentPreviewProps {
  title?: string;
  src?: string; // optional URL to document (image/pdf)
  children?: React.ReactNode; // optional custom content fallback
  onClose?: () => void;
  onDownload?: () => void;
  showOpenInNewTab?: boolean;
}

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const Modal = styled.div`
  width: min(900px, 90vw);
  height: min(70vh, 800px);
  background: white;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing[4]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray200};
`;

const Title = styled.h3`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  color: ${({ theme }) => theme.colors.gray900};
`;

const Body = styled.div`
  flex: 1;
  background: ${({ theme }) => theme.colors.gray0};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing[4]};
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const Frame = styled.iframe`
  width: 100%;
  height: 100%;
  min-height: 420px;
  border: none;
  background: white;
`;

export const DocumentPreview: React.FC<DocumentPreviewProps> = ({
  title = 'Document Preview',
  src,
  children,
  onClose,
  onDownload,
  showOpenInNewTab = true,
}) => {
  const isImage = Boolean(src && src.match(/\.(png|jpg|jpeg|gif|svg)(\?|#|$)/i));

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title>{title}</Title>
          <HeaderActions>
            {src && showOpenInNewTab && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => window.open(src, '_blank', 'noopener,noreferrer')}
              >
                Open in New Tab
              </Button>
            )}
            {onDownload && (
              <Button size="sm" variant="outline" onClick={onDownload}>
                Download
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={onClose}>Close</Button>
          </HeaderActions>
        </Header>
        <Body>
          {src ? (
            // Try to handle images and PDFs via iframe/embed; fallback to link
            isImage ? (
              <img src={src} alt={title} style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: 8 }} />
            ) : (
              <Frame src={src} title={title} />
            )
          ) : (
            children || <div style={{ color: '#475569', fontSize: 14 }}>No preview available.</div>
          )}
        </Body>
      </Modal>
    </Overlay>
  );
};
